/* The department's effective skill catalogue, and the two questions every consumer asks
 * of it: what is this cell called, and which cell is this really?
 *
 * Both answers used to be written twice — `buildSkills()` in ui/data/skills.js for the
 * two SPAs, and a private `getSkills()` in embertracking/api/src/data/tasks.js for the
 * expiry-reminder emails. The copies had drifted: the api's knew nothing about
 * `trainingProvider`, so a VIERA department's qualification emails resolved their labels
 * against the JIBC catalogue and shipped a subject line naming a cell the member doesn't
 * have. They read from here now.
 *
 * A skill definition is one of three shapes:
 *
 *   'Water Supply'                     — a bare label
 *   {label: 'S-185', width: 210, …}    — a full definition
 *   {skill: 'teamLeader.engineBoss'}   — an ALIAS
 *
 * An ALIAS says "this cell is the same credential as that one, listed under two
 * categories because both want to show it". Reads and writes resolve to the path named
 * by `skill`, while the level roll-up skips the alias rather than double-counting it.
 * An alias may carry keys of its own, and those win over the target's.
 *
 * `oldMappings` is the opposite relation, and the distinction matters. It says "this path
 * USED to be called that", so the old path is not a second cell at all — it is this cell,
 * spelled the way a previous release spelled it. A renamed skill leaves its old path on
 * every user doc that had it, and nothing can clear it: the catalogue no longer lists it,
 * so the matrix can't draw a cell for it. That unreachable copy is what #529 was — it
 * raised its own EXPIRY task beside the current one, and the member got two overdue
 * emails for one credential.
 *
 * This declares renames and resolves them for labels and task keys. Compatibility reads
 * for moved shared qualifications live in sharedQualificationPaths.js.
 *
 * Deliberately takes the catalogues as an argument rather than requiring them: they live
 * in ./index.js, which requires this module.
 */

const get = require('lodash/get');

// Must match DEFAULT_TRAINING_PROVIDER in common/qualificationLevels.js — a department
// with no explicit provider gets the JIBC catalogue everywhere or nowhere.
const DEFAULT_TRAINING_PROVIDER = 'jibc';

// The oldest records store a bare label string where newer ones store a definition map.
const asSkillDef = def => (typeof def === 'string' ? {label: def} : (def || {}));

// Follow an alias to the entry it names, with the alias's own keys winning. Returns the
// definition unchanged when it isn't one, so callers can run everything through it.
const resolveSkillDef = (def, catalogue = {}) => {
    const own = asSkillDef(def);
    if (!own.skill) {
        return own;
    }

    const [level, key] = own.skill.split('.');
    const target = asSkillDef(get(catalogue, `${level}.skills.${key}`));

    return {...target, ...own, label: own.label || target.label || key};
};

// Reverse index over every `oldMappings` declaration in a catalogue: former path → the
// path that replaced it. Built once per catalogue rather than per lookup — the expiry
// trigger canonicalises inside a loop over every skill on the user doc.
const buildRenamedSkillIndex = (catalogue = {}) => {
    const index = {};

    for (const level of Object.getOwnPropertyNames(catalogue)) {
        const levelDef = catalogue[level];
        for (const [key, def] of Object.entries(levelDef?.skills || {})) {
            for (const oldPath of asSkillDef(def).oldMappings || []) {
                index[oldPath] = `${level}.${key}`;
            }
        }
    }

    return index;
};

// Strip the `skills.` prefix the matrix uses for form field names, so a caller can pass
// either `skills.jibc.evaluator` or `jibc.evaluator`.
const bareSkillPath = field => String(field || '').replace(/^skills\./, '');

// Two catalogues merged a level at a time drops the levels they share: VIERA and JIBC
// both define `exterior`, so a shallow spread means whichever loses can't resolve ANY of
// its cells. Legacy data is exactly the cross-provider case — a department that switched
// providers, or a cell that was moved between them — so the union has to reach inside
// each level and merge the skill maps too. Only ever consulted when the department's own
// catalogue has no entry, so it can't change what a live cell resolves to.
const unionCatalogues = (...catalogues) => {
    const union = {};

    for (const catalogue of catalogues) {
        for (const [level, levelDef] of Object.entries(catalogue || {})) {
            union[level] = {
                ...union[level],
                ...levelDef,
                skills: {...union[level]?.skills, ...levelDef?.skills}
            };
        }
    }

    return union;
};

// A department's extra qualification categories (settings/general), in catalogue shape.
// These are authored as {uid, label} pairs in the UI, so every skill is a bare label —
// a custom category can't declare an alias or a rename.
const buildCustomCategories = (qualificationAdditionalCategories = []) =>
    qualificationAdditionalCategories.reduce((result, category) => {
        const {uid, label, skills = []} = category;

        if (skills.length) {
            result[uid] = {
                label,
                extra: true,
                custom: true,
                skills: skills.reduce((acc, skill) => {
                    acc[skill.uid] = skill.label;
                    return acc;
                }, {})
            };
        }

        return result;
    }, {});

// The catalogue a department actually renders, plus the lookups over it.
//
// `catalogues` is the provider catalogues by key, supplied by ./index.js. Callers pass
// the department's `trainingProvider` and `qualificationAdditionalCategories` from
// settings/general; both are optional, and with neither this is the plain JIBC catalogue.
const createSkillCatalog = ({qualificationAdditionalCategories = [], trainingProvider, catalogues = {}} = {}) => {
    const providerSkills = catalogues[trainingProvider] || catalogues[DEFAULT_TRAINING_PROVIDER] || {};
    const customSkills = buildCustomCategories(qualificationAdditionalCategories);

    const skills = {...providerSkills, ...customSkills};

    // Where a NAME is looked up, in order: the department's own catalogue first, then
    // every other provider. Rendering only ever uses `skills` above — this exists so a
    // label can still be found for a cell the department's provider doesn't define,
    // which is what legacy data is. A department that switched providers, or a
    // credential that lives under one provider's path while the other reaches it through
    // an alias, leaves expiry tasks pointing at paths its own catalogue has never heard
    // of, and an unnamed task reads as a blank subject line in the reminder email.
    //
    // A cell is named by the catalogue that DEFINES it — its level label and its skill
    // label come from the same place. That keeps a credential both providers share
    // reading identically on either.
    // Taking the level label from the department's catalogue and the skill label from
    // wherever it was found would splice the two into a name neither one gives.
    const namingCatalogues = [skills, ...Object.values(catalogues)];

    const findSkill = (level, key) =>
        namingCatalogues.find(catalogue => get(catalogue, `${level}.skills.${key}`) !== undefined);

    const findDisplayAlias = path => {
        for (const catalogue of namingCatalogues) {
            for (const [level, category] of Object.entries(catalogue || {})) {
                if (category?.storageOnly) continue;
                for (const definition of Object.values(category?.skills || {})) {
                    if (definition && typeof definition === 'object' && definition.skill === path) {
                        return {catalogue, level, category, definition};
                    }
                }
            }
        }
        return null;
    };

    // Renames are declared on the built-in catalogues only, but index them all so a path
    // retired from one provider still resolves on a department running the other.
    const renamed = namingCatalogues.reduce((all, catalogue) => ({...all, ...buildRenamedSkillIndex(catalogue)}), {});

    // Passes a falsy field straight back rather than normalising it to '': callers hand
    // this whole rows of tasks, most of which aren't qualification tasks at all, and a
    // task that had no skill must come back with no skill.
    const canonicalSkillPath = field => {
        if (!field) {
            return field;
        }

        const path = bareSkillPath(field);
        return renamed[path] || path;
    };

    const getSkillLabel = field => {
        if (!field) {
            return '';
        }

        const [level, key] = canonicalSkillPath(field).split('.');
        const source = findSkill(level, key);
        const displayAlias = source?.[level]?.storageOnly
            ? findDisplayAlias(`${level}.${key}`)
            : null;

        const levelLabel = displayAlias?.category?.label
            || get(source, `${level}.label`)
            || get(skills, `${level}.label`);
        const {
            label: skillLabel,
            hasExpiry = false,
            theoryPractical = false,
            theoryOnly = false,
            exam,
            chapter
        } = displayAlias
            ? resolveSkillDef(displayAlias.definition, displayAlias.catalogue)
            : resolveSkillDef(get(source, `${level}.skills.${key}`), source);

        return {
            // A cell the catalogue has never heard of still has to read as something in an
            // email subject, so fall back to its key rather than composing "undefined".
            label: [levelLabel, skillLabel || key].filter(Boolean).join(' - '),
            levelLabel,
            skillLabel,
            hasExpiry,
            theoryPractical: !!theoryPractical,
            theoryOnly: !!theoryOnly,
            exam,
            chapter
        };
    };

    return {skills, getSkillLabel, canonicalSkillPath};
};

module.exports = {
    DEFAULT_TRAINING_PROVIDER,
    asSkillDef,
    resolveSkillDef,
    buildRenamedSkillIndex,
    bareSkillPath,
    unionCatalogues,
    buildCustomCategories,
    createSkillCatalog
};
